import { Link } from "wouter";

export default function Methodology() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 text-sm leading-relaxed text-slate-300">
      <div>
        <h1 className="text-lg font-semibold text-white">Methodology</h1>
        <p className="mt-1 text-xs text-slate-500">How MACROSCOPE turns raw macro data into context.</p>
      </div>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-macro-accent">Surprise Engine</h2>
        <p>
          The Surprise Engine scores how much a released economic number deviated from what the recent trend implied.
          It is <strong>not</strong> a market forecast and does not tell you to buy or sell.
        </p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <strong>Expectation:</strong> For each release, we compute a simple trend expectation from the prior 3
            observations (change extrapolation). Where free consensus data exists, you can toggle to consensus view.
          </li>
          <li>
            <strong>Surprise z-score:</strong> We measure how far the actual release was from the expectation, scaled by
            the recent standard deviation of surprises. A z of +2 means the release beat the trend by two standard
            deviations; -2 means it missed by the same amount.
          </li>
          <li>
            <strong>Decay:</strong> Older releases count less. A CPI print from three months ago contributes less to the
            current country score than a print from last week.
          </li>
          <li>
            <strong>Aggregation:</strong> Event-level z-scores are weighted by impact and combined into a country score
            (-100 to +100). The Global gauge is a GDP/FX-weighted blend of the country scores.
          </li>
        </ol>
        <p className="text-xs text-slate-500">
          Source: FRED first-release vintages + trend model. Consensus toggle is populated only when a free consensus
          source is available; otherwise it shows a coverage gap.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-macro-accent">Macro Weather</h2>
        <p>
          Macro Weather fuses the headline instruments, event calendar, positioning extremes, liquidity trend, and
          geopolitical tension into a single persona-specific read. Each persona weights the inputs differently: a FX
          day trader cares more about DXY and FX pairs; an index scalper cares more about SPX and VIX.
        </p>
        <p className="text-xs text-slate-500">
          It is an educational summary of cross-asset conditions, not a trading signal.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-macro-accent">Data quality & honesty</h2>
        <ul className="list-disc space-y-2 pl-5">
          <li>Every value carries a source, an updated-at timestamp, and a delayed flag.</li>
          <li>Coverage gaps and low-confidence outputs are labeled, not hidden.</li>
          <li>Free consensus is used when available; otherwise we show a labeled model expectation.</li>
          <li>Positioning data is weekly and lagged by definition.</li>
          <li>All outputs are educational context. Nothing here is financial advice.</li>
        </ul>
      </section>

      <div className="pt-4">
        <Link href="/" className="text-xs text-macro-accent hover:underline">
          ← Back to dashboard
        </Link>
      </div>
    </div>
  );
}
